// src/shared/components/widgets/DailyLineGraph.tsx
import React from 'react'
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Line,
} from 'recharts'
import { format, startOfDay, endOfDay } from 'date-fns'

interface DailyLineGraphProps<T extends Record<string, any>> {
  /** The data array. Each item must have a timestamp field matching `xKey`. */
  data: T[]
  /** Which key on each datum holds the ISO-8601 timestamp string. */
  xKey: keyof T
  /** Which numeric fields to plot as lines. */
  yKeys: Array<keyof T>
  /** Human-readable labels for each series (optional). */
  labels?: string[]
  /** The date for which to fix the x-axis from 00:00→23:59. */
  day: Date
  /** Width and height of the container (defaults to full width, 200px high). */
  width?: string | number
  height?: number
  /** Show dots, grid, legend, etc. */
  showDots?: boolean
  showGrid?: boolean
  showLegend?: boolean
}

export default function DailyLineGraph<T extends Record<string, any>>({
  data,
  xKey,
  yKeys,
  labels,
  day,
  width = '100%',
  height = 200,
  showDots = false,
  showGrid = true,
  showLegend = false,
}: DailyLineGraphProps<T>) {
  // compute fixed domain boundaries (ms since epoch)
  const startMs = startOfDay(day).getTime()
  const endMs   = endOfDay(day).getTime()

  // map your data so that x values become numeric timestamps
  const chartData = data.map((d) => ({
    ...d,
    __ts: new Date(String(d[xKey])).getTime(),
  }))

    // fallback pastel palette
  const defaultPalette = [
    '#82C3EC',
    '#A8D5BA',
    '#F5CDA7',
    '#D6A4E7',
    '#F7A6A6',
    '#F9E79F',
    '#B9AEDC',
    '#AED6F1',
  ]

  return (
    <div style={{ width, height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData}>
          {showGrid && <CartesianGrid stroke="#374151" strokeDasharray="3 3" />}

          <XAxis
            dataKey="__ts"
            type="number"
            domain={[startMs, endMs]}
            tickLine={false}
            axisLine={{ stroke: '#4b5563' }}
            tickFormatter={(ms) => format(ms, 'HH:mm')}
            tick={{ fill: '#9ca3af', fontSize: 12 }}
          />

          <YAxis
            tickLine={false}
            axisLine={{ stroke: '#4b5563' }}
            tick={{ fill: '#9ca3af', fontSize: 12 }}
            label={{
              value: labels && labels.length === 1 ? labels[0] : 'Value',
              angle: -90,
              position: 'insideLeft',
              fill: '#9ca3af',
              fontSize: 12,
            }}
            domain={['auto', 'auto']}
          />

          <Tooltip
            labelFormatter={(ms) => format(ms as number, "HH:mm:ss")}
            contentStyle={{
              backgroundColor: '#1f2937',
              border: 'none',
              borderRadius: 4,
              color: '#f9fafb',
              fontSize: 12,
            }}
          />

          {showLegend && <Legend wrapperStyle={{ color: '#9ca3af', fontSize: 12 }} />}

          {yKeys.map((yKey, idx) => {
            const strokeColor = defaultPalette[idx % defaultPalette.length]
            const name = labels?.[idx] ?? String(yKey)
            return (
              <Line
                key={`${String(yKey)}-${idx}`}
                type="monotone"
                dataKey={String(yKey)}
                stroke={strokeColor}
                strokeWidth={2}
                connectNulls={true}
                dot={{
                  r: showDots ? 4 : 0,
                  strokeWidth: showDots ? 2 : 0,
                  fill: 'transparent',
                }}
                activeDot={{
                  r: showDots ? 6 : 0,
                  strokeWidth: showDots ? 3 : 0,
                  fill: 'transparent',
                }}
                name={name}
                isAnimationActive={false}
              />
            )
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
